'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId, MarketParams } from '@morpho-org/blue-sdk';
import type { InterfaceToken } from '@morpho-org/dapp-core';
import type { VaultV2BlueReallocation } from '@morpho-org/morpho-sdk';
import { ZERO } from '@morpho-org/dapp-core';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { Button, Icon } from '@repo/ui';
import { Alert, Close } from '@repo/ui/icons';
import { memo, useMemo, useState } from 'react';
import { useWalletClient } from 'wagmi';
import MarketRecapV2 from '@/components/Bundler/ConfirmDialog/Recaps/MarketRecapV2';
import { useIsProtectedLltvPending, useProtectedLltv } from '@/components/common/AutoDeleverage/ProtectedLltv';
import CloseToLiquidationConfirmBox from '@/components/common/CloseToLiquidationConfirmBox/CloseToLiquidationConfirmBox';
import { ConfirmDialogV2 } from '@/components/ConfirmDialogV2';
import { useTransactionContextV2 } from '@/contexts/Transaction';
import { useWeb3Context } from '@/contexts/Web3Context';
import { getRequirementsErrorMessage } from '@/helpers/requirementErrors';
import { isSharedLiquiditySimulationUnverified as getIsSharedLiquiditySimulationUnverified, submitUnlessDeadlineExpired } from '@/hooks/operation/market/v2/marketAction.helpers';
import { getMarketActionAlertMessage } from '@/hooks/operation/market/v2/marketActionError';
import { useMarketRefinanceBuilder } from '@/hooks/operation/market/v2/useMarketRefinanceBuilder';
import { useMarketRefinanceSimulatedFinalPosition } from '@/hooks/operation/market/v2/useMarketRefinanceSimulatedFinalPosition';
import useCloseDialogOnWalletChange from '@/hooks/useCloseDialogOnWalletChange';
import { useReviewConfirmGate } from '@/hooks/useDeferredSubmit';
import { useReviewFunnelTracking } from '@/lib/analytics/client/hooks/useReviewFunnelTracking';
import { buildReviewDialogParams } from '@/lib/analytics/client/trackers/checkout-funnel';
import { getSdkTracking } from '@/lib/observability/client/trackers/transaction/sdk';
import { logger } from '@/lib/observability/logger';
import { useLogSdkBuildFailed } from '@/lib/observability/sdk';
import { tryBuildSimulationAuthorizations } from '@/services/simulate/simulatedPosition.helpers';
import type { RefinanceDialogMarket } from './toRefinanceDialogMarket';
import { getRefinanceFinalPositionRisk } from './refinanceRisk';
import { useRefinancePositionPreview } from './useRefinancePositionPreview';
export interface RefinanceReviewDialogProps {
  chainId: ChainId;
  sourceMarketParams: MarketParams;
  targetMarketParams: MarketParams;
  apiMarketFrom: RefinanceDialogMarket;
  apiMarketTo: RefinanceDialogMarket;
  collateralToken: InterfaceToken;
  loanToken: InterfaceToken;
  targetReallocations?: readonly VaultV2BlueReallocation[];
}

/**
 * Review + execution step of the refinance flow — the cross-market sister of
 * `MarketActionReviewDialogV2`. Runs the evm-simulation (both markets) that the input dialog omits,
 * hard-stops either leg at/above its protected threshold, requires acknowledgment in the warning
 * band below it (the target gains debt, the source loses collateral), then submits via
 * `submitTransactions` + `ConfirmDialogV2` with the checkout-funnel attempt context threaded through.
 */
const RefinanceReviewDialog = t0 => {
  const $ = _c(156);
  const {
    chainId,
    sourceMarketParams,
    targetMarketParams,
    apiMarketFrom,
    apiMarketTo,
    collateralToken,
    loanToken,
    targetReallocations
  } = t0;
  const {
    toggleDialog
  } = useDialogContext();
  const {
    data: client
  } = useWalletClient();
  const {
    submitTransactions
  } = useTransactionContextV2();
  const {
    account,
    targetChain
  } = useWeb3Context();
  const [isRiskAcknowledged, setIsRiskAcknowledged] = useState(false);
  const [reviewSessionId] = useState(_temp);
  useCloseDialogOnWalletChange();
  let t1;
  if ($[0] !== sourceMarketParams || $[1] !== targetMarketParams || $[2] !== targetReallocations) {
    t1 = {
      sourceMarketParams,
      targetMarketParams,
      targetReallocations
    };
    $[0] = sourceMarketParams;
    $[1] = targetMarketParams;
    $[2] = targetReallocations;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    transaction,
    requirements,
    areRequirementsReady,
    isRequirementsPlaceholderData,
    simulationKey: builtSimulationKey,
    sourcePositionData,
    targetPositionData,
    buildError,
    marketError,
    deadline,
    rebuild,
    isRefreshing: isBundleRefreshing,
    isLoading: isBuilderLoading
  } = useMarketRefinanceBuilder(t1);
  const sourceCollateral = sourcePositionData?.collateral ?? ZERO;
  const sourceDebt = sourcePositionData?.borrowAssets ?? ZERO;
  let t2;
  if ($[4] !== account.address || $[5] !== chainId || $[6] !== requirements) {
    t2 = tryBuildSimulationAuthorizations({
      requirements,
      chainId,
      from: account.address
    });
    $[4] = account.address;
    $[5] = chainId;
    $[6] = requirements;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const {
    simulationAuthorizations,
    simulationApprovalError
  } = t2;
  const t3 = buildError ?? simulationApprovalError;
  let t4;
  if ($[8] !== chainId || $[9] !== reviewSessionId || $[10] !== t3) {
    t4 = {
      sdkAction: "refinance",
      chainId,
      reviewSessionId,
      error: t3
    };
    $[8] = chainId;
    $[9] = reviewSessionId;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  useLogSdkBuildFailed(t4);
  let t5;
  if ($[12] !== sourceCollateral || $[13] !== sourceDebt || $[14] !== sourcePositionData || $[15] !== targetPositionData) {
    t5 = {
      sourcePositionData,
      targetPositionData,
      collateralAmount: sourceCollateral,
      movedDebt: sourceDebt
    };
    $[12] = sourceCollateral;
    $[13] = sourceDebt;
    $[14] = sourcePositionData;
    $[15] = targetPositionData;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const {
    sourceCurrent,
    targetCurrent,
    sourceInitial,
    targetInitial,
    sourceAfterJs,
    targetAfterJs
  } = useRefinancePositionPreview(t5);
  let t6;
  if ($[17] !== builtSimulationKey) {
    t6 = builtSimulationKey ? [builtSimulationKey] : undefined;
    $[17] = builtSimulationKey;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const simulationKey = t6;
  let t7;
  if ($[19] !== areRequirementsReady || $[20] !== chainId || $[21] !== isBuilderLoading || $[22] !== simulationAuthorizations || $[23] !== simulationKey || $[24] !== sourceAfterJs || $[25] !== sourceCurrent || $[26] !== sourceMarketParams || $[27] !== targetAfterJs || $[28] !== targetCurrent || $[29] !== targetMarketParams || $[30] !== transaction) {
    t7 = {
      chainId,
      sourceMarketParams,
      targetMarketParams,
      transaction,
      simulationAuthorizations,
      simulationKey,
      isPreparing: isBuilderLoading,
      areRequirementsReady,
      sourceCurrent,
      targetCurrent,
      sourceFallback: sourceAfterJs,
      targetFallback: targetAfterJs
    };
    $[19] = areRequirementsReady;
    $[20] = chainId;
    $[21] = isBuilderLoading;
    $[22] = simulationAuthorizations;
    $[23] = simulationKey;
    $[24] = sourceAfterJs;
    $[25] = sourceCurrent;
    $[26] = sourceMarketParams;
    $[27] = targetAfterJs;
    $[28] = targetCurrent;
    $[29] = targetMarketParams;
    $[30] = transaction;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  const {
    source: sourceFinal,
    target: targetFinal,
    status: simulationStatus,
    isLoading: isSimLoading,
    isRefreshing: isSimRefreshing
  } = useMarketRefinanceSimulatedFinalPosition(t7);
  const isSimulationBlacklisted = simulationStatus === "blacklist";
  const t8 = !!targetReallocations?.length;
  let t9;
  if ($[32] !== simulationStatus || $[33] !== t8) {
    t9 = getIsSharedLiquiditySimulationUnverified({
      hasReallocations: t8,
      status: simulationStatus
    });
    $[32] = simulationStatus;
    $[33] = t8;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  const isSharedLiquiditySimulationUnverified = t9;
  let t10;
  if ($[35] !== chainId || $[36] !== sourceMarketParams.id || $[37] !== sourceMarketParams.lltv) {
    t10 = {
      lltv: sourceMarketParams.lltv,
      marketId: sourceMarketParams.id,
      chainId
    };
    $[35] = chainId;
    $[36] = sourceMarketParams.id;
    $[37] = sourceMarketParams.lltv;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  const sourceProtectedLltv = useProtectedLltv(t10);
  let t11;
  if ($[39] !== chainId || $[40] !== targetMarketParams.id || $[41] !== targetMarketParams.lltv) {
    t11 = {
      lltv: targetMarketParams.lltv,
      marketId: targetMarketParams.id,
      chainId
    };
    $[39] = chainId;
    $[40] = targetMarketParams.id;
    $[41] = targetMarketParams.lltv;
    $[42] = t11;
  } else {
    t11 = $[42];
  }
  const targetProtectedLltv = useProtectedLltv(t11);
  let t12;
  if ($[43] !== chainId || $[44] !== sourceMarketParams.id) {
    t12 = {
      marketId: sourceMarketParams.id,
      chainId
    };
    $[43] = chainId;
    $[44] = sourceMarketParams.id;
    $[45] = t12;
  } else {
    t12 = $[45];
  }
  const isSourceProtectedLltvPending = useIsProtectedLltvPending(t12);
  let t13;
  if ($[46] !== chainId || $[47] !== targetMarketParams.id) {
    t13 = {
      marketId: targetMarketParams.id,
      chainId
    };
    $[46] = chainId;
    $[47] = targetMarketParams.id;
    $[48] = t13;
  } else {
    t13 = $[48];
  }
  const isTargetProtectedLltvPending = useIsProtectedLltvPending(t13);
  let t14;
  if ($[49] !== isSimLoading || $[50] !== sourceFinal.ltv || $[51] !== sourceProtectedLltv || $[52] !== targetFinal.ltv || $[53] !== targetProtectedLltv) {
    t14 = isSimLoading ? null : getRefinanceFinalPositionRisk({
      sourceLtv: sourceFinal.ltv,
      targetLtv: targetFinal.ltv,
      sourceProtectedLltv,
      targetProtectedLltv
    });
    $[49] = isSimLoading;
    $[50] = sourceFinal.ltv;
    $[51] = sourceProtectedLltv;
    $[52] = targetFinal.ltv;
    $[53] = targetProtectedLltv;
    $[54] = t14;
  } else {
    t14 = $[54];
  }
  const settledRisk = t14;
  let t15;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      isAtOrAboveProtectedLltv: false,
      isCloseToProtectedLltv: false
    };
    $[55] = t15;
  } else {
    t15 = $[55];
  }
  const [lastSettledRisk, setLastSettledRisk] = useState(t15);
  if (settledRisk && (settledRisk.isAtOrAboveProtectedLltv !== lastSettledRisk.isAtOrAboveProtectedLltv || settledRisk.isCloseToProtectedLltv !== lastSettledRisk.isCloseToProtectedLltv)) {
    setLastSettledRisk(settledRisk);
  }
  const finalPositionRisk = settledRisk ?? lastSettledRisk;
  const isHardBlocked = !client || !account.isWalletTypeResolved || account.isWrongChain || isSourceProtectedLltvPending || isTargetProtectedLltvPending || isSimulationBlacklisted || isSharedLiquiditySimulationUnverified || !!buildError || !!marketError || simulationApprovalError !== undefined || finalPositionRisk.isAtOrAboveProtectedLltv || finalPositionRisk.isCloseToProtectedLltv && !isRiskAcknowledged;
  const isPrepared = !!transaction && areRequirementsReady && !isBuilderLoading && !isSimLoading;
  let t16;
  if ($[56] !== sourceFinal.borrowAssets || $[57] !== sourceFinal.collateral || $[58] !== sourceFinal.ltv) {
    t16 = {
      collateral: sourceFinal.collateral,
      borrowAssets: sourceFinal.borrowAssets,
      ltv: sourceFinal.ltv
    };
    $[56] = sourceFinal.borrowAssets;
    $[57] = sourceFinal.collateral;
    $[58] = sourceFinal.ltv;
    $[59] = t16;
  } else {
    t16 = $[59];
  }
  const t17 = apiMarketFrom.state?.avgBorrowApy;
  const t18 = !sourceCurrent;
  const t19 = isSimLoading || !sourceCurrent;
  let t20;
  if ($[60] !== apiMarketFrom || $[61] !== chainId || $[62] !== collateralToken || $[63] !== loanToken || $[64] !== sourceCollateral || $[65] !== sourceDebt || $[66] !== sourceFinal.isUnverifiedFinalPosition || $[67] !== sourceInitial || $[68] !== sourceMarketParams.lltv || $[69] !== t16 || $[70] !== t17 || $[71] !== t18 || $[72] !== t19) {
    t20 = <MarketRecapV2 marketId={apiMarketFrom.marketId} chainId={chainId} collateralAsset={collateralToken} loanAsset={loanToken} mode="repayWithdraw" collateralAmount={sourceCollateral} loanAmount={sourceDebt} initial={sourceInitial} final={t16} lltv={sourceMarketParams.lltv} marketForRewards={apiMarketFrom} avgBorrowApy={t17} isInitialPositionLoading={t18} isFinalPositionLoading={t19} isSimulationFallback={sourceFinal.isUnverifiedFinalPosition} />;
    $[60] = apiMarketFrom;
    $[61] = chainId;
    $[62] = collateralToken;
    $[63] = loanToken;
    $[64] = sourceCollateral;
    $[65] = sourceDebt;
    $[66] = sourceFinal.isUnverifiedFinalPosition;
    $[67] = sourceInitial;
    $[68] = sourceMarketParams.lltv;
    $[69] = t16;
    $[70] = t17;
    $[71] = t18;
    $[72] = t19;
    $[73] = t20;
  } else {
    t20 = $[73];
  }
  const fromRecap = t20;
  let t21;
  if ($[74] !== targetFinal.borrowAssets || $[75] !== targetFinal.collateral || $[76] !== targetFinal.ltv) {
    t21 = {
      collateral: targetFinal.collateral,
      borrowAssets: targetFinal.borrowAssets,
      ltv: targetFinal.ltv
    };
    $[74] = targetFinal.borrowAssets;
    $[75] = targetFinal.collateral;
    $[76] = targetFinal.ltv;
    $[77] = t21;
  } else {
    t21 = $[77];
  }
  const t22 = apiMarketTo.state?.avgBorrowApy;
  const t23 = !targetCurrent;
  const t24 = isSimLoading || !targetCurrent;
  let t25;
  if ($[78] !== apiMarketTo || $[79] !== chainId || $[80] !== collateralToken || $[81] !== loanToken || $[82] !== sourceCollateral || $[83] !== sourceDebt || $[84] !== t21 || $[85] !== t22 || $[86] !== t23 || $[87] !== t24 || $[88] !== targetFinal.isUnverifiedFinalPosition || $[89] !== targetInitial || $[90] !== targetMarketParams.lltv) {
    t25 = <MarketRecapV2 marketId={apiMarketTo.marketId} chainId={chainId} collateralAsset={collateralToken} loanAsset={loanToken} mode="supplyBorrow" collateralAmount={sourceCollateral} loanAmount={sourceDebt} initial={targetInitial} final={t21} lltv={targetMarketParams.lltv} marketForRewards={apiMarketTo} avgBorrowApy={t22} isInitialPositionLoading={t23} isFinalPositionLoading={t24} isSimulationFallback={targetFinal.isUnverifiedFinalPosition} />;
    $[78] = apiMarketTo;
    $[79] = chainId;
    $[80] = collateralToken;
    $[81] = loanToken;
    $[82] = sourceCollateral;
    $[83] = sourceDebt;
    $[84] = t21;
    $[85] = t22;
    $[86] = t23;
    $[87] = t24;
    $[88] = targetFinal.isUnverifiedFinalPosition;
    $[89] = targetInitial;
    $[90] = targetMarketParams.lltv;
    $[91] = t25;
  } else {
    t25 = $[91];
  }
  const toRecap = t25;
  let t26;
  if ($[92] !== fromRecap || $[93] !== toRecap) {
    t26 = <div className="gap-md flex flex-col">{fromRecap}{toRecap}</div>;
    $[92] = fromRecap;
    $[93] = toRecap;
    $[94] = t26;
  } else {
    t26 = $[94];
  }
  const recap = t26;
  let t27;
  if ($[95] !== account.isSafe || $[96] !== requirements || $[97] !== reviewSessionId) {
    t27 = buildReviewDialogParams({
      reviewSessionId,
      entryPoint: "refinance",
      operationTypes: ["refinance"],
      isSafe: account.isSafe,
      requirements
    });
    $[95] = account.isSafe;
    $[96] = requirements;
    $[97] = reviewSessionId;
    $[98] = t27;
  } else {
    t27 = $[98];
  }
  const reviewTrackingParams = t27;
  const {
    trackClose,
    confirmAttempt
  } = useReviewFunnelTracking(reviewTrackingParams, !isBuilderLoading);
  let t28;
  if ($[99] !== toggleDialog || $[100] !== trackClose) {
    t28 = () => {
      trackClose();
      toggleDialog(false);
    };
    $[99] = toggleDialog;
    $[100] = trackClose;
    $[101] = t28;
  } else {
    t28 = $[101];
  }
  const handleClose = t28;
  let t29;
  if ($[102] !== account || $[103] !== areRequirementsReady || $[104] !== client || $[105] !== collateralToken || $[106] !== confirmAttempt || $[107] !== deadline || $[108] !== isHardBlocked || $[109] !== rebuild || $[110] !== recap || $[111] !== requirements || $[112] !== submitTransactions || $[113] !== targetChain || $[114] !== toggleDialog || $[115] !== transaction) {
    t29 = () => {
      if (!transaction || !client || !areRequirementsReady || isHardBlocked) {
        return;
      }
      submitUnlessDeadlineExpired({
        deadline,
        rebuild,
        submit: () => {
          submitTransactions(client, {
            actionType: "refinance",
            flow: "refinance",
            transaction,
            ...getSdkTracking(transaction),
            requirements,
            chain: targetChain,
            account,
            checkoutTracking: confirmAttempt()
          }).catch(error => {
            logger.error(error instanceof Error ? error : new Error(String(error)), {
              tags: {
                component: "RefinanceReviewDialog"
              }
            });
            toggleDialog(false);
          });
          toggleDialog(<ConfirmDialogV2 txMessage="Execute your refinance" token={collateralToken} operationContent={recap} />, {
            closable: false
          });
        }
      });
    };
    $[102] = account;
    $[103] = areRequirementsReady;
    $[104] = client;
    $[105] = collateralToken;
    $[106] = confirmAttempt;
    $[107] = deadline;
    $[108] = isHardBlocked;
    $[109] = rebuild;
    $[110] = recap;
    $[111] = requirements;
    $[112] = submitTransactions;
    $[113] = targetChain;
    $[114] = toggleDialog;
    $[115] = transaction;
    $[116] = t29;
  } else {
    t29 = $[116];
  }
  const handleConfirm = t29;
  const t30 = isRequirementsPlaceholderData || isBundleRefreshing || isSimRefreshing;
  let t31;
  if ($[117] !== handleConfirm || $[118] !== isHardBlocked || $[119] !== isPrepared || $[120] !== t30) {
    t31 = {
      isHardBlocked,
      isPrepared,
      isRefreshSignal: t30,
      submit: handleConfirm
    };
    $[117] = handleConfirm;
    $[118] = isHardBlocked;
    $[119] = isPrepared;
    $[120] = t30;
    $[121] = t31;
  } else {
    t31 = $[121];
  }
  const {
    request: requestConfirm,
    isDisabled: isConfirmDisabled,
    isRefreshing: isConfirmRefreshing
  } = useReviewConfirmGate(t31);
  let t32;
  if ($[122] !== buildError || $[123] !== marketError) {
    t32 = getMarketActionAlertMessage({
      buildError,
      marketError
    });
    $[122] = buildError;
    $[123] = marketError;
    $[124] = t32;
  } else {
    t32 = $[124];
  }
  const alertMessage = t32;
  let t33;
  if ($[125] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <span className="text-title-sm">Review</span>;
    $[125] = t33;
  } else {
    t33 = $[125];
  }
  let t34;
  if ($[126] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <Close />;
    $[126] = t34;
  } else {
    t34 = $[126];
  }
  let t35;
  if ($[127] !== handleClose) {
    t35 = <div className="flex items-center justify-between gap-[16px]">{t33}<Button size="icon" onClick={handleClose} variant="secondary" aria-label="Close">{t34}</Button></div>;
    $[127] = handleClose;
    $[128] = t35;
  } else {
    t35 = $[128];
  }
  let t36;
  if ($[129] !== alertMessage || $[130] !== isBuilderLoading) {
    t36 = !isBuilderLoading && alertMessage && <div className="gap-2xs flex flex-row items-center" role="alert"><Icon className="text-destructive-foreground" icon={<Alert />} /><span className="text-destructive-foreground text-xs">{alertMessage}</span></div>;
    $[129] = alertMessage;
    $[130] = isBuilderLoading;
    $[131] = t36;
  } else {
    t36 = $[131];
  }
  let t37;
  if ($[132] !== simulationApprovalError) {
    t37 = simulationApprovalError && <div className="gap-2xs flex flex-row items-center" role="alert"><Icon className="text-destructive-foreground" icon={<Alert />} /><span className="text-destructive-foreground text-xs">{getRequirementsErrorMessage(simulationApprovalError)}</span></div>;
    $[132] = simulationApprovalError;
    $[133] = t37;
  } else {
    t37 = $[133];
  }
  let t38;
  if ($[134] !== isSimulationBlacklisted) {
    t38 = isSimulationBlacklisted && <div className="gap-2xs flex flex-row items-center" role="alert" data-testid="refinance-blacklist-error"><Icon className="text-destructive-foreground" icon={<Alert />} /><span className="text-destructive-foreground text-xs">This transaction can't be completed due to a simulation error.</span></div>;
    $[134] = isSimulationBlacklisted;
    $[135] = t38;
  } else {
    t38 = $[135];
  }
  let t39;
  if ($[136] !== finalPositionRisk.isAtOrAboveProtectedLltv) {
    t39 = finalPositionRisk.isAtOrAboveProtectedLltv && <div className="gap-2xs flex flex-row items-center" role="alert"><Icon className="text-destructive-foreground" icon={<Alert />} /><span className="text-destructive-foreground text-xs">This refinance would put a position at or above its protected threshold.</span></div>;
    $[136] = finalPositionRisk.isAtOrAboveProtectedLltv;
    $[137] = t39;
  } else {
    t39 = $[137];
  }
  let t40;
  if ($[138] !== finalPositionRisk.isCloseToProtectedLltv || $[139] !== isRiskAcknowledged) {
    t40 = finalPositionRisk.isCloseToProtectedLltv && <CloseToLiquidationConfirmBox isRiskyPositionsAcknowledged={isRiskAcknowledged} setIsRiskyPositionsAcknowledged={setIsRiskAcknowledged} />;
    $[138] = finalPositionRisk.isCloseToProtectedLltv;
    $[139] = isRiskAcknowledged;
    $[140] = t40;
  } else {
    t40 = $[140];
  }
  let t41;
  if ($[141] !== requestConfirm) {
    t41 = () => requestConfirm();
    $[141] = requestConfirm;
    $[142] = t41;
  } else {
    t41 = $[142];
  }
  let t42;
  if ($[143] !== isConfirmDisabled || $[144] !== isConfirmRefreshing || $[145] !== t41) {
    t42 = <Button data-testid="confirm-button" data-refreshing={isConfirmRefreshing} size="lg" disabled={isConfirmDisabled} onClick={t41}>Refinance</Button>;
    $[143] = isConfirmDisabled;
    $[144] = isConfirmRefreshing;
    $[145] = t41;
    $[146] = t42;
  } else {
    t42 = $[146];
  }
  let t43;
  if ($[147] !== recap || $[148] !== t35 || $[149] !== t36 || $[150] !== t37 || $[151] !== t38 || $[152] !== t39 || $[153] !== t40 || $[154] !== t42) {
    t43 = <div className="gap-lg flex flex-col" data-testid="confirm-dialog">{t35}{recap}{t36}{t37}{t38}{t39}{t40}{t42}</div>;
    $[147] = recap;
    $[148] = t35;
    $[149] = t36;
    $[150] = t37;
    $[151] = t38;
    $[152] = t39;
    $[153] = t40;
    $[154] = t42;
    $[155] = t43;
  } else {
    t43 = $[155];
  }
  return t43;
};
export default memo(RefinanceReviewDialog);
function _temp() {
  return crypto.randomUUID();
}